import React, { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, ArrowLeft, CheckCircle2, AlertTriangle, Scale } from "lucide-react";

const KAPPA_TONE = (k) => {
  if (k == null) return "ek-text-muted";
  if (k >= 0.81) return "text-emerald-700";
  if (k >= 0.61) return "text-emerald-600";
  if (k >= 0.41) return "text-amber-600";
  return "text-red-600";
};

function Metric({ label, value, sub, tone }) {
  return (
    <div className="border ek-line ek-surface p-4 rounded-lg" data-testid={`irr-metric-${label}`}>
      <div className="text-[10.5px] font-medium tracking-[0.16em] ek-text-muted uppercase mb-1">{label}</div>
      <div className={`text-2xl font-medium tabular-nums ${tone || "text-[#7737F6]"}`}>{value}</div>
      {sub ? <div className="text-[11px] ek-text-muted mt-1">{sub}</div> : null}
    </div>
  );
}

function Agreement({ a }) {
  if (!a?.ready) {
    return (
      <div className="border ek-line ek-surface-2 rounded-lg p-4 text-[13px] ek-text-muted" data-testid="irr-not-ready">
        {a?.message || "Add at least two raters to compute agreement."}
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="irr-agreement">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Kappa" value={a.quadratic_weighted_kappa ?? "—"} sub={a.kappa_interpretation}
                tone={KAPPA_TONE(a.quadratic_weighted_kappa)} />
        <Metric label="Agreement" value={a.pct_agreement != null ? `${a.pct_agreement}%` : "—"}
                sub={`within ±${a.tolerance}`} />
        <Metric label="Mean diff" value={a.mean_absolute_difference ?? "—"} sub="avg |a−b|" />
        <Metric label="Raters" value={a.n_raters} sub={`${a.n_comparisons} comparisons`} />
      </div>
      <div>
        <div className="text-[12px] font-semibold tracking-[0.14em] uppercase ek-text-muted mb-2">
          Per-item spread {a.flagged_items?.length ? `· ${a.flagged_items.length} flagged` : ""}
        </div>
        <div className="border ek-line ek-surface rounded-lg divide-y divide-zinc-100">
          {a.per_item.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`irr-item-${p.id}`}>
              <span className="text-[13px] ek-text flex items-center gap-2">
                {p.flag
                  ? <AlertTriangle size={14} className="text-amber-500" />
                  : <CheckCircle2 size={14} className="text-emerald-500" />}
                {p.name}
              </span>
              <span className="text-[12px] ek-text-soft tabular-nums">
                {p.values.join(" · ")} <span className="ek-text-muted">(range {p.range})</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScoreEntry({ session, onSubmitted }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [scores, setScores] = useState({});
  const [busy, setBusy] = useState(false);
  const cfg = session.scale_cfg || { min: 0, max: 100 };

  const submit = async () => {
    if (!email.trim()) return toast.error("Rater email required");
    const clean = {};
    for (const it of session.items) {
      if (scores[it.id] !== undefined && scores[it.id] !== "") clean[it.id] = Number(scores[it.id]);
    }
    if (!Object.keys(clean).length) return toast.error("Enter at least one score");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/irr/sessions/${session.id}/score`,
        { rater_email: email.trim(), rater_name: name.trim(), scores: clean });
      toast.success("Scores recorded");
      setEmail(""); setName(""); setScores({});
      onSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save scores");
    } finally { setBusy(false); }
  };

  return (
    <div className="border ek-line ek-surface-2 rounded-lg p-4 space-y-3" data-testid="irr-score-entry">
      <div className="text-[13px] font-semibold ek-text">Add a rater's independent scores</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="irr-rater-email" placeholder="Rater email" value={email}
               onChange={(e) => setEmail(e.target.value)}
               className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface" />
        <input data-testid="irr-rater-name" placeholder="Rater name (optional)" value={name}
               onChange={(e) => setName(e.target.value)}
               className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface" />
      </div>
      <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
        {session.items.map((it) => (
          <label key={it.id} className="flex items-center justify-between gap-2 text-[12.5px] ek-text-soft">
            <span className="truncate">{it.name}</span>
            <input type="number" min={cfg.min} max={cfg.max} step="1"
                   data-testid={`irr-score-${it.id}`}
                   value={scores[it.id] ?? ""}
                   onChange={(e) => setScores((s) => ({ ...s, [it.id]: e.target.value }))}
                   className="w-20 border ek-line rounded px-2 py-1 text-sm ek-surface tabular-nums" />
          </label>
        ))}
      </div>
      <div className="text-[11px] ek-text-muted">Scale: {cfg.min}–{cfg.max}. Agreement counts within ±{cfg.tolerance}.</div>
      <button onClick={submit} disabled={busy} data-testid="irr-submit-scores"
              className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg disabled:opacity-60">
        {busy ? "Saving…" : "Save scores"}
      </button>
    </div>
  );
}

function SessionDetail({ id, onBack }) {
  const [s, setS] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/irr/sessions/${id}`);
      setS(data);
    } catch { toast.error("Could not load session"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!s) return <div className="ek-text-muted text-sm py-8">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="irr-session-detail">
      <button onClick={onBack} data-testid="irr-back"
              className="flex items-center gap-1.5 text-sm ek-text-muted hover:text-zinc-800">
        <ArrowLeft size={15} /> Back to sessions
      </button>
      <div>
        <h3 className="ek-heading text-lg">{s.title}</h3>
        <p className="text-[12.5px] ek-text-muted mt-1">
          {s.subject_type === "e360_streams" ? "Enterprise360 value-stream maturity" : "EKSELENS360 assessment dimensions"}
          {" · "}{s.items.length} items · {s.raters.length} rater(s)
        </p>
      </div>
      <Agreement a={s.agreement} />
      <ScoreEntry session={s} onSubmitted={setS} />
    </div>
  );
}

export default function InterRaterPanel() {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [frameworks, setFrameworks] = useState([]);
  const [form, setForm] = useState({ title: "", subject_type: "e360_streams", framework_id: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/irr/sessions");
      setRows(data.sessions || []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/assessments/frameworks").then(({ data }) => setFrameworks(data.items || [])).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.subject_type === "ekselens_dims" && !form.framework_id) return toast.error("Pick a framework");
    try {
      const { data } = await api.post("/admin/irr/sessions", {
        title: form.title.trim(), subject_type: form.subject_type,
        framework_id: form.subject_type === "ekselens_dims" ? form.framework_id : null,
      });
      toast.success("Calibration session created");
      setShowNew(false); setForm({ title: "", subject_type: "e360_streams", framework_id: "" });
      setOpenId(data.id); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create session"); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/irr/sessions/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  if (openId) return <div className="ek-admin"><SessionDetail id={openId} onBack={() => { setOpenId(null); load(); }} /></div>;

  return (
    <div className="ek-admin space-y-5" data-testid="irr-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2"><Scale size={20} /> Inter-rater reliability</h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            Prove two consultants score the same subject alike. Create a calibration session, have each
            consultant score independently, and review agreement (weighted kappa, % agreement, mean difference).
          </p>
        </div>
        <button onClick={() => setShowNew((v) => !v)} data-testid="irr-new-session-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm bg-[#7737F6] text-white rounded-lg shrink-0">
          <Plus size={15} /> New session
        </button>
      </div>

      {showNew && (
        <div className="border ek-line ek-surface-2 rounded-lg p-4 grid sm:grid-cols-2 gap-3" data-testid="irr-new-form">
          <input data-testid="irr-title" placeholder="Session title" value={form.title}
                 onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                 className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface" />
          <select data-testid="irr-subject-type" value={form.subject_type}
                  onChange={(e) => setForm((f) => ({ ...f, subject_type: e.target.value }))}
                  className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface">
            <option value="e360_streams">Enterprise360 value streams</option>
            <option value="ekselens_dims">EKSELENS360 assessment dimensions</option>
          </select>
          {form.subject_type === "ekselens_dims" && (
            <select data-testid="irr-framework" value={form.framework_id}
                    onChange={(e) => setForm((f) => ({ ...f, framework_id: e.target.value }))}
                    className="border ek-line rounded-lg px-3 py-2 text-sm ek-surface sm:col-span-2">
              <option value="">Select a framework…</option>
              {frameworks.map((fw) => <option key={fw.id} value={fw.id}>{fw.name}</option>)}
            </select>
          )}
          <div className="sm:col-span-2">
            <button onClick={create} data-testid="irr-create"
                    className="px-4 py-2 text-sm bg-[#7737F6] text-white rounded-lg">Create session</button>
          </div>
        </div>
      )}

      {rows === null ? (
        <div className="ek-text-muted text-sm py-8">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border ek-line ek-surface-2 rounded-lg p-8 text-center ek-text-muted text-sm" data-testid="irr-empty">
          <Users size={22} className="mx-auto mb-2 opacity-60" />
          No calibration sessions yet. Create one to start proving score consistency.
        </div>
      ) : (
        <div className="border ek-line ek-surface rounded-lg divide-y divide-zinc-100">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`irr-row-${r.id}`}>
              <button onClick={() => setOpenId(r.id)} className="text-left flex-1" data-testid={`irr-open-${r.id}`}>
                <div className="text-[14px] font-medium ek-text">{r.title}</div>
                <div className="text-[12px] ek-text-muted mt-0.5">
                  {r.subject_type === "e360_streams" ? "E360 streams" : "EKSELENS360 dims"} · {r.n_raters} rater(s)
                </div>
              </button>
              <div className="flex items-center gap-4">
                {r.ready ? (
                  <span className={`text-[13px] font-medium tabular-nums ${KAPPA_TONE(r.kappa)}`} data-testid={`irr-kappa-${r.id}`}>
                    κ {r.kappa} · {r.pct_agreement}%
                  </span>
                ) : <span className="text-[12px] ek-text-muted">awaiting raters</span>}
                <button onClick={() => del(r.id)} data-testid={`irr-delete-${r.id}`}
                        className="ek-text-muted hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
